import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Grid, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  List, 
  ListItem, 
  ListItemText, 
  ListItemIcon, 
  Divider, 
  CircularProgress,
  Chip,
  Avatar,
  useTheme,
  Paper
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon, 
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Event as EventIcon,
  Inventory as InventoryIcon,
  ListAlt as ListAltIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { usePublicDisplay } from '../components/PublicDisplayWrapper';
import { format, parseISO, isAfter, isBefore, addDays, isSameDay, differenceInCalendarDays } from 'date-fns';
import config from '../config';

function DisplayPage({ isPublic = false, hardcodedToken = null }) {
  const { token } = useAuth();
  // Always call the hook, but only use its value if isPublic is true
  const publicDisplayContext = usePublicDisplay();
  // Use hardcoded token if provided, otherwise use the context token
  const publicToken = isPublic ? (hardcodedToken || publicDisplayContext.publicToken) : null;
  const theme = useTheme();
  
  // State for each app's data
  const [todos, setTodos] = useState([]);
  const [timeOffs, setTimeOffs] = useState([]);
  const [cycleCounts, setCycleCounts] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState({
    todos: true,
    timeOffs: true,
    cycleCounts: true
  });

  // Error states
  const [errors, setErrors] = useState({
    todos: null,
    timeOffs: null,
    cycleCounts: null
  });

  // Current time for the clock
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update the time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => clearInterval(timer);
  }, []);

  // Use useCallback to memoize the fetch functions
  const fetchTodos = useCallback(async () => {
    try {
      // Use different endpoints based on whether this is public or authenticated
      const endpoint = isPublic && publicToken
        ? `${config.apiUrl}/public/todos?token=${publicToken}`
        : `${config.apiUrl}/todos`;
      
      const headers = isPublic && publicToken
        ? { 'Content-Type': 'application/json' }
        : { 'Authorization': `Bearer ${token}` };
      
      console.log('Fetching todos from:', endpoint);
      const response = await fetch(endpoint, { headers });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(`API error: ${response.status} ${errorData.error || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('Raw todos data from API:', data);
      
      // Sort by priority and completion status
      const sortedTodos = data.sort((a, b) => {
        if (a.completed === b.completed) {
          return 0;
        }
        return a.completed ? 1 : -1;
      });
      
      setTodos(sortedTodos);
      setLoading(prev => ({ ...prev, todos: false }));
    } catch (error) {
      console.error('Error fetching notes:', error);
      setErrors(prev => ({ ...prev, todos: error.message }));
      setLoading(prev => ({ ...prev, todos: false }));
    }
  }, [token, isPublic, publicToken]);

  // Add this function to filter out past time off dates
  const filterCurrentAndFutureTimeOffs = useCallback((timeOffs) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison
    
    return timeOffs.filter(timeOff => {
      // If endDate is in the future, keep it
      const endDate = parseISO(timeOff.endDate);
      return isAfter(endDate, today) || isSameDay(endDate, today);
    });
  }, []);

  // Add this function to consolidate consecutive time off dates for the same user
  const consolidateTimeOffs = useCallback((timeOffs) => {
    if (!timeOffs.length) return [];
    
    // Sort by user and start date
    const sortedTimeOffs = [...timeOffs].sort((a, b) => {
      // First sort by user
      const userA = `${a.user?.firstName} ${a.user?.lastName}`;
      const userB = `${b.user?.firstName} ${b.user?.lastName}`;
      
      if (userA !== userB) {
        return userA.localeCompare(userB);
      }
      
      // Then by start date
      return new Date(a.startDate) - new Date(b.startDate);
    });
    
    const consolidated = [];
    let current = null;
    
    for (const timeOff of sortedTimeOffs) {
      // If this is the first entry or a different user, start a new group
      if (!current || 
          current.user.firstName !== timeOff.user?.firstName || 
          current.user.lastName !== timeOff.user?.lastName) {
        
        current = {
          _id: timeOff._id,
          startDate: timeOff.startDate,
          endDate: timeOff.endDate,
          reason: timeOff.reason,
          user: {
            firstName: timeOff.user?.firstName,
            lastName: timeOff.user?.lastName,
            color: timeOff.user?.color
          }
        };
        consolidated.push(current);
        continue;
      }
      
      // Check if this entry is consecutive with the current one
      const currentEndDate = parseISO(current.endDate);
      const nextStartDate = parseISO(timeOff.startDate);
      
      // If the next start date is one day after the current end date, extend the range
      if (differenceInCalendarDays(nextStartDate, currentEndDate) === 1) {
        current.endDate = timeOff.endDate;
        // Combine reasons if they're different and not empty
        if (timeOff.reason && timeOff.reason !== current.reason) {
          current.reason = current.reason 
            ? `${current.reason}, ${timeOff.reason}` 
            : timeOff.reason;
        }
      } else {
        // Not consecutive, start a new entry
        current = {
          _id: timeOff._id,
          startDate: timeOff.startDate,
          endDate: timeOff.endDate,
          reason: timeOff.reason,
          user: {
            firstName: timeOff.user?.firstName,
            lastName: timeOff.user?.lastName,
            color: timeOff.user?.color
          }
        };
        consolidated.push(current);
      }
    }
    
    return consolidated;
  }, []);

  // Update the fetchTimeOffs function to use this consolidation
  const fetchTimeOffs = useCallback(async () => {
    try {
      // Use different endpoints based on whether this is public or authenticated
      let endpoint, headers;
      
      if (isPublic && publicToken) {
        endpoint = `${config.apiUrl}/public/timeoff/dashboard?token=${publicToken}`;
        headers = { 'Content-Type': 'application/json' };
        console.log('Using public endpoint for time off:', endpoint);
      } else {
        endpoint = `${config.apiUrl}/timeoff/dashboard`;
        headers = { 'Authorization': `Bearer ${token}` };
        console.log('Using authenticated endpoint for time off:', endpoint);
      }
      
      const response = await fetch(endpoint, { headers });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(`API error: ${response.status} ${errorData.error || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('Raw time off data from API:', data);
      
      // Filter to only show current and future time off
      const filteredTimeOffs = filterCurrentAndFutureTimeOffs(data);
      
      // Consolidate consecutive dates for the same user
      const consolidatedTimeOffs = consolidateTimeOffs(filteredTimeOffs);
      
      // Sort by start date
      const sortedTimeOffs = consolidatedTimeOffs.sort((a, b) => {
        return new Date(a.startDate) - new Date(b.startDate);
      });
      
      setTimeOffs(sortedTimeOffs);
      setLoading(prev => ({ ...prev, timeOffs: false }));
    } catch (error) {
      console.error('Error fetching time off requests:', error);
      setErrors(prev => ({ ...prev, timeOffs: error.message }));
      setLoading(prev => ({ ...prev, timeOffs: false }));
    }
  }, [token, filterCurrentAndFutureTimeOffs, consolidateTimeOffs, isPublic, publicToken]);

  const fetchCycleCounts = useCallback(async () => {
    try {
      // Use different endpoints based on whether this is public or authenticated
      let endpoint, headers;
      
      if (isPublic && publicToken) {
        endpoint = `${config.apiUrl}/public/cyclecount?token=${publicToken}`;
        headers = { 'Content-Type': 'application/json' };
        console.log('Using public endpoint for cycle counts:', endpoint);
      } else {
        endpoint = `${config.apiUrl}/cyclecount`;
        headers = { 'Authorization': `Bearer ${token}` };
        console.log('Using authenticated endpoint for cycle counts:', endpoint);
      }
      
      const response = await fetch(endpoint, { headers });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(`API error: ${response.status} ${errorData.error || 'Unknown error'}`);
      }
      
      const data = await response.json();
      console.log('Raw cycle count data from API:', data);
      
      // Sort by status (open first) and then by reportedAt date
      const sortedCycleCounts = data.sort((a, b) => {
        if (a.status === b.status) {
          return new Date(b.reportedAt) - new Date(a.reportedAt);
        }
        return a.status === 'open' ? -1 : 1;
      });
      
      setCycleCounts(sortedCycleCounts);
      setLoading(prev => ({ ...prev, cycleCounts: false }));
    } catch (error) {
      console.error('Error fetching cycle counts:', error);
      setErrors(prev => ({ ...prev, cycleCounts: error.message }));
      setLoading(prev => ({ ...prev, cycleCounts: false }));
    }
  }, [token, isPublic, publicToken]);

  useEffect(() => {
    // Fetch data from all apps
    fetchTodos();
    fetchTimeOffs();
    fetchCycleCounts();
    
    // Set up refresh interval (every 5 minutes)
    const refreshInterval = setInterval(() => {
      fetchTodos();
      fetchTimeOffs();
      fetchCycleCounts();
    }, 5 * 60 * 1000);
    
    return () => clearInterval(refreshInterval);
  }, [fetchTodos, fetchTimeOffs, fetchCycleCounts]);

  // Helper function to format date ranges
  const formatDateRange = (startDateString, endDateString) => {
    if (!startDateString || !endDateString) return '';
    
    const startDate = parseISO(startDateString);
    const endDate = parseISO(endDateString);
    
    // If start and end dates are the same, just show one date
    if (isSameDay(startDate, endDate)) {
      return format(startDate, 'MMM d, yyyy');
    }
    
    // Otherwise show a range
    return `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`;
  };

  return (
    <Box sx={{ 
      flexGrow: 1, 
      p: 4, 
      height: '100vh', 
      overflow: 'hidden',
      bgcolor: '#f5f5f5'
    }}>
      {/* Header with time and date */}
      <Paper 
        elevation={3} 
        sx={{ 
          p: 2, 
          mb: 3, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: theme.palette.primary.main,
          color: 'white'
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          Company Information Display
        </Typography>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="h3" fontWeight="bold">
            {format(currentTime, 'h:mm a')}
          </Typography>
          <Typography variant="h6">
            {format(currentTime, 'EEEE, MMMM d, yyyy')}
          </Typography>
        </Box>
      </Paper>
      
      <Grid container spacing={3} sx={{ height: 'calc(100% - 100px)' }}>
        {/* Note Board Section */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Card 
            elevation={3}
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderTop: `8px solid ${theme.palette.primary.main}`
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ListAltIcon sx={{ mr: 1, color: theme.palette.primary.main, fontSize: 30 }} />
                  <Typography variant="h5">Note Board</Typography>
                </Box>
              }
              sx={{ pb: 1 }}
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
              {loading.todos ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : errors.todos ? (
                <Typography color="error" sx={{ p: 2 }}>{errors.todos}</Typography>
              ) : todos.length === 0 ? (
                <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', p: 4 }}>
                  No notes found.
                </Typography>
              ) : (
                <List>
                  {todos.slice(0, 10).map((todo) => (
                    <React.Fragment key={todo._id}>
                      <ListItem sx={{ py: 2, px: 3 }}>
                        <ListItemIcon>
                          {todo.completed ? (
                            <CheckCircleIcon color="success" sx={{ fontSize: 28 }} />
                          ) : (
                            <RadioButtonUncheckedIcon color="action" sx={{ fontSize: 28 }} />
                          )}
                        </ListItemIcon>
                        <ListItemText 
                          primary={
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                textDecoration: todo.completed ? 'line-through' : 'none',
                                color: todo.completed ? 'text.secondary' : 'text.primary',
                                fontWeight: todo.completed ? 'normal' : 'medium'
                              }}
                            >
                              {todo.text}
                            </Typography>
                          }
                        />
                      </ListItem>
                      <Divider variant="inset" component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Time Off Section */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Card 
            elevation={3}
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderTop: `8px solid ${theme.palette.secondary.main}`
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EventIcon sx={{ mr: 1, color: theme.palette.secondary.main, fontSize: 30 }} />
                  <Typography variant="h5">Time Off Schedule</Typography>
                </Box>
              }
              sx={{ pb: 1 }}
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
              {loading.timeOffs ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : errors.timeOffs ? (
                <Typography color="error" sx={{ p: 2 }}>{errors.timeOffs}</Typography>
              ) : timeOffs.length === 0 ? (
                <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', p: 4 }}>
                  No upcoming time off.
                </Typography>
              ) : (
                <List>
                  {timeOffs.map((timeOff) => (
                    <React.Fragment key={timeOff._id}>
                      <ListItem sx={{ py: 2, px: 3 }}>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Avatar 
                              sx={{ 
                                width: 36, 
                                height: 36, 
                                bgcolor: timeOff.user?.color || theme.palette.primary.main,
                                fontSize: '1rem',
                                mr: 2
                              }}
                            >
                              {timeOff.user?.firstName?.charAt(0) || 'U'}
                            </Avatar>
                            <Typography variant="h6">
                              {timeOff.user?.firstName} {timeOff.user?.lastName}
                            </Typography>
                          </Box>
                          <Box sx={{ ml: 7 }}>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDateRange(timeOff.startDate, timeOff.endDate)}
                            </Typography>
                            {timeOff.reason && (
                              <Typography variant="body2" color="text.secondary">
                                {timeOff.reason}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Cycle Count Section */}
        <Grid item xs={12} md={4} sx={{ height: '100%' }}>
          <Card 
            elevation={3}
            sx={{ 
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              borderTop: `8px solid ${theme.palette.error.main}`
            }}
          >
            <CardHeader
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <InventoryIcon sx={{ mr: 1, color: theme.palette.error.main, fontSize: 30 }} />
                  <Typography variant="h5">Inventory Counts</Typography>
                </Box>
              }
              sx={{ pb: 1 }}
            />
            <Divider />
            <CardContent sx={{ flexGrow: 1, overflow: 'auto', p: 0 }}>
              {loading.cycleCounts ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : errors.cycleCounts ? (
                <Typography color="error" sx={{ p: 2 }}>{errors.cycleCounts}</Typography>
              ) : cycleCounts.length === 0 ? (
                <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', p: 4 }}>
                  No cycle counts found.
                </Typography>
              ) : (
                <List>
                  {cycleCounts.slice(0, 10).map((cycleCount) => (
                    <React.Fragment key={cycleCount._id}>
                      <ListItem sx={{ py: 2, px: 3 }}>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" fontWeight="medium">
                              {cycleCount.productId || cycleCount.partNumber}
                            </Typography>
                            <Chip 
                              label={cycleCount.status.toUpperCase()} 
                              sx={{ 
                                bgcolor: cycleCount.status === 'open' ? theme.palette.warning.main : theme.palette.success.main,
                                color: 'white',
                                fontWeight: 'bold',
                                fontSize: '0.8rem'
                              }}
                            />
                          </Box>
                          <Typography variant="body1">
                            Location: {cycleCount.location}
                          </Typography>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            mt: 1
                          }}>
                            <Typography variant="body2">
                              Expected: {cycleCount.expectedQty || cycleCount.reportedQty}
                            </Typography>
                            {cycleCount.status === 'completed' && (
                              <>
                                <Typography variant="body2">
                                  Actual: {cycleCount.actualQty}
                                </Typography>
                                {cycleCount.actualQty !== undefined && cycleCount.reportedQty !== undefined && (
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 'bold',
                                      color: cycleCount.actualQty > cycleCount.reportedQty 
                                        ? theme.palette.success.main 
                                        : cycleCount.actualQty < cycleCount.reportedQty 
                                          ? theme.palette.error.main 
                                          : 'text.secondary'
                                    }}
                                  >
                                    Diff: {cycleCount.actualQty - cycleCount.reportedQty > 0 ? '+' : ''}
                                    {cycleCount.actualQty - cycleCount.reportedQty}
                                  </Typography>
                                )}
                              </>
                            )}
                          </Box>
                        </Box>
                      </ListItem>
                      <Divider component="li" />
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default DisplayPage; 